import { useState, useEffect } from 'react'
import { Loader2, Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { FileUploadDropzone } from '@/components/PlanWizard/FileUploadDropzone'
import { downloadDummySkuTemplate, replaceDummySkus } from '@/api/skus'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import { toast } from '@/stores/useToastStore'
import type { ProcessingResponse } from '@/types'

type Step = 'upload' | 'processing'

interface ReplaceDummySkusModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  lineItemIds: string[]
  onReplaceComplete: () => void
}

export function ReplaceDummySkusModal({
  open,
  onOpenChange,
  planId,
  lineItemIds,
  onReplaceComplete,
}: ReplaceDummySkusModalProps) {
  const [step, setStep] = useState<Step>('upload')
  const [error, setError] = useState<string | null>(null)
  const [processingResponse, setProcessingResponse] =
    useState<ProcessingResponse | null>(null)

  const processing = useProcessingStatus(processingResponse)

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('upload')
      setError(null)
      setProcessingResponse(null)
      processing.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Handle processing completion or error
  useEffect(() => {
    if (processing.state === 'complete') {
      toast.success('Dummy SKUs replaced successfully')
      onReplaceComplete()
      onOpenChange(false)
    } else if (processing.state === 'error' && step === 'processing') {
      toast.error(processing.errorMessage ?? 'Failed to replace SKUs')
      setError(processing.errorMessage)
      setProcessingResponse(null)
      processing.reset()
      setStep('upload')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.state])

  const handleDownloadTemplate = async () => {
    try {
      await downloadDummySkuTemplate(lineItemIds)
    } catch {
      toast.error('Failed to download template')
    }
  }

  const handleFileSelect = async (file: File) => {
    setStep('processing')
    setError(null)
    try {
      const response = await replaceDummySkus(planId, file)
      setProcessingResponse(response)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to upload file'
      toast.error(message)
      setError(message)
      setStep('upload')
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (step === 'processing') return
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Replace Dummy SKUs</DialogTitle>
          <DialogDescription>
            {step === 'upload' &&
              'Download the template, fill in the new SKU numbers and UPCs, then upload the file.'}
            {step === 'processing' && 'Replacing dummy SKUs...'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 overflow-y-auto min-h-0 max-h-[60vh]">
          {/* Upload Step */}
          {step === 'upload' && (
            <div>
              <FileUploadDropzone
                onFileSelect={handleFileSelect}
                isLoading={false}
                error={error}
              />
              <p className="text-[13px] text-[#556374] mt-3">
                <button
                  onClick={handleDownloadTemplate}
                  className="text-[#CF1F2E] hover:underline cursor-pointer inline-flex items-center gap-1"
                >
                  <Download className="h-3 w-3" />
                  Download template
                </button>
              </p>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="w-full max-w-md space-y-2">
                <Progress value={processing.progress} className="h-2" />
                <p className="text-center text-sm text-muted-foreground">
                  {processing.stage || 'Processing...'}
                  {processing.progress > 0 && ` ${processing.progress}%`}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'upload' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
